/**
 * ScalePad asset helpers.
 *
 * ScalePad-MCP `list_hardware_assets` emits a NESTED row:
 *   { warranty: { status, is_active, end_date, days_remaining },
 *     age: { purchase_date, age_years }, risk: { level, factors, score },
 *     manufacturer: { id, name } | null, model: { number, description } | null,
 *     serial_number, ... }
 * The ScalePad page and Assets widget read FLAT camelCase fields. Map here, once, so the
 * page never reads a key the tool doesn't send (which is how every row rendered
 * Unknown / Unknown / -- until 2026-09-25).
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Flatten one `list_hardware_assets` row onto the fields ScalePadPage reads.
 * Unknown / missing nested objects become `undefined`, never throw.
 */
export const normalizeScalePadAsset = (asset = {}) => {
  const warranty = asset.warranty || {};
  const age = asset.age || {};
  const risk = asset.risk || {};
  const manufacturer = asset.manufacturer;
  const model = asset.model;

  return {
    ...asset,
    manufacturer: manufacturer && typeof manufacturer === 'object' ? manufacturer.name || undefined : manufacturer || undefined,
    model: model && typeof model === 'object' ? model.description || model.number || undefined : model || undefined,
    serialNumber: asset.serial_number ?? asset.serialNumber ?? undefined,
    warrantyStatus: warranty.status,
    warrantyDaysRemaining: warranty.days_remaining,
    warrantyExpiryDate: warranty.end_date,
    riskLevel: risk.level,
    purchaseDate: age.purchase_date,
    ageYears: age.age_years,
  };
};

/**
 * Classify a warranty for display: { status, color, text }.
 *
 * `daysRemaining` wins whenever it is a number. The tool reports status as active / expired /
 * unknown only, so "Expiring Soon" and "< 90 Days" can only come from the day count; the
 * page's old helper returned Active on the status string first and never reached them,
 * which left the "Warranty Expiring" stat card at 0. Colours are antd Tag names, not hex.
 */
export const classifyWarranty = (warrantyStatus, daysRemaining) => {
  if (typeof daysRemaining === 'number' && !Number.isNaN(daysRemaining)) {
    if (daysRemaining < 0) return { status: 'expired', color: 'red', text: 'Expired' };
    if (daysRemaining <= 30) return { status: 'expiring', color: 'orange', text: 'Expiring Soon' };
    if (daysRemaining <= 90) return { status: 'warning', color: 'gold', text: '< 90 Days' };
    return { status: 'active', color: 'green', text: 'Active' };
  }

  const status = typeof warrantyStatus === 'string' ? warrantyStatus.toLowerCase() : '';
  if (status === 'expired' || status === 'out_of_warranty') {
    return { status: 'expired', color: 'red', text: 'Expired' };
  }
  if (status === 'expiring' || status === 'expiring_soon') {
    return { status: 'expiring', color: 'orange', text: 'Expiring Soon' };
  }
  if (status === 'active' || status === 'in_warranty') {
    return { status: 'active', color: 'green', text: 'Active' };
  }
  return { status: 'unknown', color: 'default', text: 'Unknown' };
};

/**
 * Warranty counts from `query_hardware_lifecycle` rows for the Assets widget.
 * A row's warranty carries `status` + `end_date`; expiring = active and ≤ 90 days left.
 */
export const summarizeLifecycleWarranties = (lifecycles, now = new Date()) => {
  const counts = { warrantyExpiring: 0, warrantyExpired: 0, underContract: 0 };
  if (!Array.isArray(lifecycles)) return counts;

  for (const row of lifecycles) {
    const warranty = row?.warranty;
    if (!warranty || !warranty.end_date) continue;

    const end = new Date(warranty.end_date);
    if (Number.isNaN(end.getTime())) continue;

    const daysRemaining = Math.floor((end.getTime() - now.getTime()) / MS_PER_DAY);
    if (daysRemaining < 0) {
      counts.warrantyExpired += 1;
    } else {
      counts.underContract += 1;
      if (daysRemaining <= 90) counts.warrantyExpiring += 1;
    }
  }

  return counts;
};
